/**
 * Boot telemetry — captura falhas críticas de carregamento do app:
 *  1. Erros de chunk dinâmico (ChunkLoadError / "Failed to fetch dynamically imported module")
 *  2. Tela em branco na landing (root sem children após timeout)
 *  3. Falhas de carregamento de assets via <script>/<link>
 *
 * Reporta para `client_error_logs` com severity="fatal" e metadata.kind
 * para facilitar dashboards/alertas. Faz auto-reload uma única vez quando
 * detecta chunk inválido em cache (cenário típico após deploy).
 */
import { logClientError } from "./error-logger";

const RELOAD_FLAG = "__boot_chunk_reloaded__";
const BLANK_TIMEOUT_MS = 8_000;

const isChunkLoadMessage = (msg?: string | null): boolean => {
  if (!msg) return false;
  return (
    msg.includes("ChunkLoadError") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
};

const reportFatal = (kind: string, message: string, extra: Record<string, unknown> = {}) => {
  void logClientError({
    message: `[boot] ${kind}: ${message}`.slice(0, 1000),
    severity: "fatal",
    metadata: {
      kind,
      route: typeof window !== "undefined" ? window.location?.pathname : null,
      url: typeof window !== "undefined" ? window.location?.href : null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
      timeFromBootMs:
        typeof performance !== "undefined" && performance.now ? Math.round(performance.now()) : null,
      ...extra,
    },
  });
};

const tryReloadOnce = (): boolean => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === "1") return false;
    sessionStorage.setItem(RELOAD_FLAG, "1");
    // Pequeno delay para o report enfileirar antes do reload
    setTimeout(() => window.location.reload(), 250);
    return true;
  } catch {
    return false;
  }
};

let installed = false;

export const installBootTelemetry = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Limpa flag de reload se o app conseguiu bootar normalmente
  // (rodamos após mount real abaixo, em scheduleBlankScreenCheck).

  // 1) Capture erros de chunk via window.onerror
  window.addEventListener("error", (event) => {
    const err = event.error as Error | undefined;
    const msg = err?.message || event.message || "";
    if (isChunkLoadMessage(msg)) {
      const reloaded = tryReloadOnce();
      reportFatal("chunk_load_error", msg, {
        stack: err?.stack?.slice(0, 4000),
        filename: event.filename,
        autoReload: reloaded,
      });
      return;
    }

    // Falha de asset: <script src=...> ou <link href=...> que não carregou
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
      const src =
        (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || "unknown";
      reportFatal("asset_load_error", `${target.tagName.toLowerCase()} failed: ${src}`, {
        tagName: target.tagName,
        src,
      });
    }
  }, true);

  // 2) Capture chunk errors em unhandledrejection
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : "";
    if (isChunkLoadMessage(msg)) {
      const reloaded = tryReloadOnce();
      reportFatal("chunk_load_rejection", msg, {
        stack: reason instanceof Error ? reason.stack?.slice(0, 4000) : undefined,
        autoReload: reloaded,
      });
    }
  });
};

/**
 * Verifica se o root React montou conteúdo dentro de BLANK_TIMEOUT_MS.
 * Se não montou, reporta como tela em branco (provável falha de boot que
 * não emitiu erro síncrono — ex.: erro em ESM que travou parser).
 */
export const scheduleBlankScreenCheck = (rootId = "root"): void => {
  if (typeof window === "undefined") return;

  const startedAt = performance.now();

  const cleanupReloadFlag = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
  };

  window.setTimeout(() => {
    const root = document.getElementById(rootId);
    const hasContent = !!root && root.children.length > 0;
    if (hasContent) {
      cleanupReloadFlag();
      return;
    }

    reportFatal("blank_screen", `root#${rootId} empty after ${BLANK_TIMEOUT_MS}ms`, {
      rootExists: !!root,
      bodyChildren: document.body?.children.length ?? 0,
      readyState: document.readyState,
      elapsedMs: Math.round(performance.now() - startedAt),
    });
  }, BLANK_TIMEOUT_MS);
};
